import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Row, Col, InputGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faExternalLinkAlt, faTv, faDesktop, faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';

/**
 * InstallationManager - Panel for generating and managing Totem and Visor URLs.
 * Helps administrators quickly install and configure client-facing screens.
 */
const InstallationManager = ({ ipserv, branch_id }) => {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState('');
    const [subStatus, setSubStatus] = useState(null);

    // Pre-calculate origin for full URLs
    const origin = window.location.origin + '/tufilaapp';

    const loadSubscription = () => {
        request.Get(ipserv + 'turnero/subscription-status', {}, r => {
            if (r.success) setSubStatus(r);
        });
    };

    const loadTypes = () => {
        setLoading(true);
        request.Get(ipserv + 'getTurneroTipos', { sucursalId: branch_id }, r => {
            if (r.success) {
                setTypes(r.rows || []);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        if (branch_id) {
            loadTypes();
            loadSubscription();
        }
    }, [branch_id]);

    const handleCopy = (url) => {
        navigator.clipboard.writeText(url);
        setCopiedUrl(url);
        Notify({ type: 'success', text: 'URL copiada al portapapeles' });
        setTimeout(() => setCopiedUrl(''), 2000);
    };

    // Construct common URLs
    const visorMultiUrl = types.length > 0
        ? `${origin}/TurneroView/MULTI?types=${types.map(t => t.TipoId).join(',')}&sucursalId=${branch_id}`
        : '';
    const totemGeneralUrl = `${origin}/TurneroTotem?sucursalId=${branch_id}`;

    if (!branch_id) {
        return (
            <div className="text-center py-5 text-muted animate-in">
                <FontAwesomeIcon icon={faInfoCircle} size="3x" className="mb-3 opacity-20" />
                <h5>Debe seleccionar una sucursal para generar las URLs de instalación.</h5>
            </div>
        );
    }

    return (
        <div className="animate-in">
            <Row>
                {/* Panel de Visores */}
                <Col lg={12} className="mb-4">
                    <div className="turnero-card p-4 h-100">
                        <div className="form-header-section mt-0 mb-4">
                            <FontAwesomeIcon icon={faTv} className="mr-2" /> Visores de Turno (Pantallas TV)
                        </div>

                        <div 
                            className={`mb-4 p-3 rounded position-relative ${subStatus && !subStatus.plan.AllowMulti ? 'opacity-50' : ''}`} 
                            style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)', overflow: 'hidden' }}
                        >
                            {subStatus && !subStatus.plan.AllowMulti && (
                                <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" style={{ top: 0, left: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 10, backdropFilter: 'blur(2px)' }}>
                                    <div className="text-center">
                                        <p className="mb-1 font-weight-bold">Módulo Premium</p>
                                        <p className="tiny text-muted m-0">Requiere Plan Premium o superior</p>
                                    </div>
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <p className="text-primary small font-weight-bold mb-0">VISOR GENERAL (MULTITIPO)</p>
                                <span className="badge badge-primary">RECOMENDADO</span>
                            </div>
                            <InputGroup>
                                <Form.Control
                                    readOnly
                                    value={visorMultiUrl}
                                    className="turnero-input  border-primary"
                                    style={{ height: '45px' }}
                                />
                                <Button
                                    className="turnero-btn turnero-btn-primary"
                                    onClick={() => handleCopy(visorMultiUrl)}
                                    disabled={!visorMultiUrl || (subStatus && !subStatus.plan.AllowMulti)}
                                >
                                    <FontAwesomeIcon icon={copiedUrl === visorMultiUrl ? faCheck : faCopy} />
                                </Button>
                            </InputGroup>
                            <small className="text-muted mt-2 d-block">
                                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                                Este visor muestra automáticamente los turnos de todos los sectores activos en esta sucursal.
                            </small>
                        </div>

                        <p className="text-muted small mb-2 font-weight-bold">INSTALACIÓN POR SECTOR INDIVIDUAL</p>
                        <Table responsive className=" bg-transparent mb-0">
                            <thead>
                                <tr>
                                    <th>Sector (Tipo)</th>
                                    <th>URL del Visor</th>
                                    <th className="text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="3" className="text-center p-4">
                                            <Spinner animation="border" size="sm" variant="primary" />
                                        </td>
                                    </tr>
                                ) : (
                                    types.map(t => {
                                        const url = `${origin}/TurneroView/${t.TipoId}?sucursalId=${branch_id}`;
                                        return (
                                            <tr key={t.TipoId}>
                                                <td className="align-middle">
                                                    <span className="font-weight-bold">{t.TipoNombre}</span>
                                                    <br />
                                                    <small className="text-muted">{t.TipoAcronimo}</small>
                                                </td>
                                                <td className="align-middle">
                                                    <code className="text-primary-light small">{url}</code>
                                                </td>
                                                <td className="text-right align-middle">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            className="turnero-btn"
                                                            style={{ background: 'var(--turnero-glass)', padding: '0.5rem' }}
                                                            onClick={() => handleCopy(url)}
                                                            title="Copiar URL"
                                                        >
                                                            <FontAwesomeIcon icon={copiedUrl === url ? faCheck : faCopy} />
                                                        </button>
                                                        <a
                                                            href={url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="turnero-btn"
                                                            style={{ background: 'var(--turnero-glass)', padding: '0.5rem' }}
                                                            title="Abrir Visor"
                                                        >
                                                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                                                        </a>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                {!loading && types.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="text-center p-4 text-muted small">No hay tipos de turno configurados para esta sucursal.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Col>

                {/* Panel de Totems */}
                <Col lg={12} className="mb-4">
                    <div className="turnero-card p-4">
                        <div className="form-header-section mt-0 mb-4">
                            <FontAwesomeIcon icon={faDesktop} className="mr-2" /> Totems de Registro (Kioskos)
                        </div>

                        <div className="mb-4">
                            <p className="text-muted small mb-2 font-weight-bold text-uppercase">Totem General de Sucursal</p>
                            <InputGroup>
                                <Form.Control
                                    readOnly
                                    value={totemGeneralUrl}
                                    className="turnero-input "
                                    style={{ height: '45px' }}
                                />
                                <Button
                                    className="turnero-btn turnero-btn-primary"
                                    onClick={() => handleCopy(totemGeneralUrl)}
                                >
                                    <FontAwesomeIcon icon={copiedUrl === totemGeneralUrl ? faCheck : faCopy} />
                                </Button>
                                <a
                                    href={totemGeneralUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="turnero-btn ml-2"
                                    style={{ background: 'var(--turnero-glass)', display: 'flex', alignItems: 'center' }}
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </a>
                            </InputGroup>
                            <small className="text-muted mt-2 d-block">
                                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                                Este enlace permite configurar un totem que muestra todos los trámites disponibles de la sucursal.
                            </small>
                        </div>
                    </div>
                </Col>

                {/* Panel de Staff (Atención) */}
                <Col lg={12}>
                    <div className="turnero-card p-4">
                        <div className="form-header-section mt-0 mb-4 text-success">
                            <FontAwesomeIcon icon={faDesktop} className="mr-2" /> Consola de Staff (Atención)
                        </div>

                        <div className="mb-4">
                            <p className="text-muted small mb-2 font-weight-bold text-uppercase">URL de Acceso para el Personal</p>
                            <InputGroup>
                                <Form.Control
                                    readOnly
                                    value={`${origin}/TurneroStaff`}
                                    className="turnero-input  border-success"
                                    style={{ height: '45px' }}
                                />
                                <Button
                                    className="turnero-btn"
                                    style={{ background: 'var(--turnero-success)', color: 'white' }}
                                    onClick={() => handleCopy(`${origin}/TurneroStaff`)}
                                >
                                    <FontAwesomeIcon icon={copiedUrl === `${origin}/TurneroStaff` ? faCheck : faCopy} />
                                </Button>
                                <a
                                    href={`${origin}/TurneroStaff`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="turnero-btn ml-2"
                                    style={{ background: 'var(--turnero-glass)', display: 'flex', alignItems: 'center' }}
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </a>
                            </InputGroup>
                            <small className="text-muted mt-2 d-block">
                                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                                Este es el acceso para que el personal de la sucursal pueda iniciar sesión y atender los turnos.
                            </small>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default InstallationManager;
